import { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ShoppingCart, Eye, ExternalLink, Download, X, AlertTriangle, FolderPlus, Package, CheckCircle, ChevronRight, Save, Plus, Trash2 } from 'lucide-react';

const STATUS_CONFIG = {
  pending:         { label: 'Pending',         color: 'bg-yellow-100 text-yellow-700' },
  acknowledged:    { label: 'Acknowledged',    color: 'bg-blue-100 text-blue-700' },
  processing:      { label: 'Processing',      color: 'bg-purple-100 text-purple-700' },
  payment_pending: { label: 'Payment Pending', color: 'bg-orange-100 text-orange-700' },
  advance_pending: { label: 'Advance Pending', color: 'bg-pink-100 text-pink-700' },
  completed:       { label: 'Completed',       color: 'bg-green-100 text-green-700' },
  cancelled:       { label: 'Cancelled',       color: 'bg-red-100 text-red-600' },
};
const PM_LABELS = { bank_transfer:'Bank Transfer', cheque:'Cheque', upi:'UPI', cash:'Cash', other:'Other' };
const UNITS = ['pcs', 'sets', 'pairs', 'nos', 'kg', 'mtr', 'sqft', 'rft'];

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); } catch { return '—'; }
}
function fmtCur(n) { return '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2 }); }

const emptyItem = () => ({ item_name: '', proto_code: '', description: '', quantity: 1, unit: 'pcs', rate: '', discount: '0' });

function calcPOTotals(items, discountAmount) {
  const subtotal = items.reduce((s, i) => s + (parseFloat(i.quantity)||0) * (parseFloat(i.rate)||0), 0);
  const discAmt = parseFloat(discountAmount) || 0;
  const taxable = subtotal - discAmt;
  return { subtotal, discAmt, taxable };
}

function parsePOItems(vd) {
  let items = [];
  if (vd?.items_json) { try { items = JSON.parse(vd.items_json); } catch {} }
  if (!items.length && vd?.item_name) {
    items = [{ item_name: vd.item_name, quantity: vd.quantity || 1, description: '', unit: 'pcs' }];
  }
  // Always map to full item shape
  return items.map(it => ({
    item_name:   it.item_name   || '',
    proto_code:  it.proto_code  || '',
    description: it.description || '',
    quantity:    it.quantity    || 1,
    unit:        it.unit        || 'pcs',
    rate:        it.rate        || '',
    discount:    it.discount    || '0',
  }));
}

export default function ClientPurchaseOrdersPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('client_pos');
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [viewId, setViewId]             = useState(null);
  const [viewData, setViewData]         = useState(null);
  const [viewLoading, setViewLoading]   = useState(false);
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [showPreview, setShowPreview]         = useState(false);
  const [previewName, setPreviewName]         = useState('');
  const [previewItems, setPreviewItems]       = useState([emptyItem()]);
  const [savingProject, setSavingProject]     = useState(false);
  const [addingItems, setAddingItems]         = useState(false);
  const [itemsImported, setItemsImported]     = useState(false);
  const [fulfillment, setFulfillment]         = useState(null);
  const [fulfillmentLoading, setFulfillmentLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/client-purchase-orders')
      .then(r => setOrders(r.data))
      .catch(() => toast.error('Load failed'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openPdf = async (url) => {
    try { const res = await fetch(url); const blob = await res.blob(); window.open(URL.createObjectURL(new Blob([blob],{type:'application/pdf'})),'_blank'); }
    catch { toast.error('Could not open PDF.'); }
  };
  const downloadPdf = async (url, filename) => {
    try {
      const res = await fetch(url); const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([blob],{type:'application/pdf'}));
      a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { toast.error('Download failed.'); }
  };

  const openView = async (id) => {
    setViewId(id); setViewLoading(true); setShowPreview(false); setItemsImported(false); setFulfillment(null);
    try {
      const r = await api.get(`/admin/client-purchase-orders/${id}`);
      setViewData(r.data);
      if (r.data.linked_project_item_count > 0) setItemsImported(true);
    }
    catch { toast.error('Failed to load details'); }
    finally { setViewLoading(false); }
    // Load fulfillment data in background
    setFulfillmentLoading(true);
    api.get(`/admin/client-purchase-orders/${id}/fulfillment`)
      .then(r => setFulfillment(r.data))
      .catch(() => {})
      .finally(() => setFulfillmentLoading(false));
  };
  const closeView = () => { setViewId(null); setViewData(null); setShowPreview(false); setFulfillment(null); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/client-purchase-orders/${id}/status`, { status });
      toast.success('Status updated'); load();
      if (viewData?.id === id) setViewData(d => ({ ...d, status }));
    } catch { toast.error('Failed'); }
  };

  const openPreview = () => {
    if (!viewData) return;
    setPreviewName(viewData.project_name || viewData.po_number || '');
    const parsed = parsePOItems(viewData);
    setPreviewItems(parsed.length ? parsed : [emptyItem()]);
    setShowPreview(true);
  };

  const updateItem = (idx, field, val) => {
    setPreviewItems(prev => {
      const u = [...prev];
      u[idx] = { ...u[idx], [field]: val };
      return u;
    });
  };
  const addItemRow    = () => setPreviewItems(prev => [...prev, emptyItem()]);
  const removeItemRow = (idx) => setPreviewItems(prev => prev.filter((_, i) => i !== idx));

  const saveAndCreateProject = async () => {
    if (!previewName.trim()) { toast.error('Project Name is required'); return; }
    const validItems = previewItems.filter(it => it.item_name.trim());
    if (!validItems.length) { toast.error('Add at least one item'); return; }
    setSavingProject(true);
    try {
      const r = await api.post(`/admin/client-purchase-orders/${viewData.id}/create-project`, {
        name: previewName.trim(),
        items: validItems,
      });
      toast.success(`✅ Project "${r.data.project_id}" created with ${validItems.length} item(s)!`);
      setShowPreview(false);
      setItemsImported(true); // project + items created together, mark as imported
      const updated = await api.get(`/admin/client-purchase-orders/${viewData.id}`);
      setViewData(updated.data); load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to create project'); }
    finally { setSavingProject(false); }
  };

  const addItemsToProject = async () => {
    if (!viewData?.linked_project_id) return;
    const items = parsePOItems(viewData);
    if (!items.length) { toast.error('No items in this PO'); return; }
    setAddingItems(true);
    try {
      await api.post(`/admin/client-purchase-orders/${viewData.id}/create-items`, {
        project_id: viewData.linked_project_id,
        items,
      });
      toast.success(`✅ ${items.length} item(s) added!`);
      setItemsImported(true);
      // Refresh view data so linked_project_item_count updates
      const updated = await api.get(`/admin/client-purchase-orders/${viewData.id}`);
      setViewData(updated.data);
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setAddingItems(false); }
  };

  const clients = [...new Set(orders.map(o => o.client_name))].sort();
  const filtered = orders.filter(o => {
    if (filterClient && o.client_name !== filterClient) return false;
    if (filterStatus && o.status !== filterStatus) return false;
    return true;
  });
  const totalPending = orders.filter(o => o.status === 'pending').length;

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-gray-900">Client Purchase Orders</h1>
          <p className="text-gray-400 text-sm mt-0.5">Orders submitted by clients</p>
        </div>
        {totalPending > 0 && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-xl text-sm font-semibold">
            <AlertTriangle className="w-4 h-4"/> {totalPending} pending review
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-3">
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="field-input" style={{maxWidth:200}}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="field-input" style={{maxWidth:180}}>
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <button onClick={() => { setFilterClient(''); setFilterStatus(''); }} className="btn-ghost text-sm">Clear</button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"/></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center"><ShoppingCart className="w-10 h-10 text-gray-200 mx-auto mb-3"/><p className="text-gray-400">No purchase orders yet</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead><tr>
                <th className="text-left">PO Number</th>
                <th className="text-left">Client</th>
                <th className="text-left">Date</th>
                <th className="text-left">Due Date</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Paid</th>
                <th className="text-center">Status</th>
                <th className="text-center">PDF</th>
                <th className="text-center">Action</th>
              </tr></thead>
              <tbody>
                {filtered.map(o => {
                  const bal = (o.total_amount||0) - (o.paid_amount||0);
                  const sc  = STATUS_CONFIG[o.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={o.id}>
                      <td className="font-mono font-bold text-gray-900">{o.po_number}</td>
                      <td className="font-medium text-gray-800">{o.client_name}</td>
                      <td className="text-gray-500">{fmt(o.order_date)}</td>
                      <td className={o.due_date && new Date(o.due_date)<new Date() && o.status!=='completed' ? 'text-red-500 font-semibold':'text-gray-500'}>{fmt(o.due_date)}</td>
                      <td className="text-right font-semibold">{fmtCur(o.total_amount)}</td>
                      <td className="text-right"><span className={bal>0?'text-red-500 font-semibold':'text-green-600 font-semibold'}>{fmtCur(o.paid_amount)}</span></td>
                      <td className="text-center">
                        <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)}
                          className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${sc.color}`}>
                          {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      </td>
                      <td className="text-center">
                        {o.pdf_url
                          ? <button onClick={() => openPdf(o.pdf_url)} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-semibold"><ExternalLink className="w-3.5 h-3.5"/> Open</button>
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="text-center">
                        <button onClick={() => openView(o.id)} className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-semibold">
                          <Eye className="w-3.5 h-3.5"/> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewId && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:720, display:'flex', flexDirection:'column', maxHeight:'92vh'}}>
              <div className="modal-header" style={{flexShrink:0}}>
                <div>
                  <h2>Purchase Order Details</h2>
                  {viewData && <p>{viewData.client_name} — PO #{viewData.po_number}</p>}
                </div>
                <button className="modal-close" onClick={closeView}><X size={16}/></button>
              </div>

              {viewLoading ? (
                <div className="modal-body flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"/>
                </div>
              ) : viewData && (
                <div className="modal-body space-y-4" style={{overflowY:'auto', flex:1}}>

                  {/* PO Info */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      ['PO Number', viewData.po_number],
                      ['Client', viewData.client_name],
                      ['Order Date', fmt(viewData.order_date)],
                      ['Due Date', fmt(viewData.due_date)],
                      ['Status', STATUS_CONFIG[viewData.status]?.label || viewData.status],
                      ['Total Amount', fmtCur(viewData.total_amount)],
                    ].map(([k,v]) => (
                      <div key={k} className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-0.5">{k}</p>
                        <p className="text-sm font-semibold text-gray-900">{v}</p>
                      </div>
                    ))}
                  </div>

                  {/* PO Items (read-only display) */}
                  {(() => {
                    const items = parsePOItems(viewData);
                    if (!items.length) return null;
                    const hasRates = items.some(it => parseFloat(it.rate) > 0);
                    const subtotal = items.reduce((s,i) => s+(parseFloat(i.quantity)||0)*(parseFloat(i.rate)||0), 0);
                    const discAmt = parseFloat(viewData.discount_amount) || 0;
                    const taxable = subtotal - discAmt;
                    return (
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <p className="text-xs font-bold text-blue-700 mb-3 uppercase tracking-wide">PO Items ({items.length})</p>
                        <div className="space-y-2">
                          {items.map((it, idx) => {
                            const lineTotal = (parseFloat(it.quantity)||0) * (parseFloat(it.rate)||0);
                            return (
                              <div key={idx} className={items.length>1?'bg-white rounded-xl p-3 border border-blue-100':''}>
                                {items.length>1 && <p className="text-xs font-bold text-gray-400 mb-1">#{idx+1}</p>}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  <div style={{gridColumn:'1/-1'}}><p className="text-xs text-gray-400">Item</p><p className="text-sm font-semibold">{it.item_name||'—'}</p></div>
                                  {it.quantity && <div><p className="text-xs text-gray-400">Qty</p><p className="text-sm font-semibold">{it.quantity} {it.unit||''}</p></div>}
                                  {parseFloat(it.rate) > 0 && <div><p className="text-xs text-gray-400">Rate</p><p className="text-sm font-semibold">{fmtCur(it.rate)}</p></div>}
                                  {parseFloat(it.rate) > 0 && <div><p className="text-xs text-gray-400">Amount</p><p className="text-sm font-semibold text-green-700">{fmtCur(lineTotal)}</p></div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Totals summary */}
                        {hasRates && (
                          <div className="mt-3 bg-white rounded-xl p-3 border border-blue-100 space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-gray-500">Subtotal (Rate × Qty)</span><span className="font-semibold">{fmtCur(subtotal)}</span></div>
                            {discAmt > 0 && <div className="flex justify-between text-red-500"><span>Discount</span><span>— {fmtCur(discAmt)}</span></div>}
                            <div className="flex justify-between font-bold text-gray-800 border-t pt-1"><span>Taxable Value</span><span>{fmtCur(taxable)}</span></div>
                            {parseFloat(viewData.total_amount) > 0 && <div className="flex justify-between font-bold text-green-700 border-t pt-1"><span>Total Amount</span><span>{fmtCur(viewData.total_amount)}</span></div>}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* ── Project Section ── */}
                  {(() => {
                    const isLinked = viewData.linked_project_id && Number(viewData.linked_project_id) > 0;

                    if (isLinked) return (
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
                          <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0"/>
                          <div>
                            <p className="text-sm font-bold text-green-800">Project Linked ✅</p>
                            <p className="text-xs text-green-600">Project ID: {viewData.linked_project_id}</p>
                          </div>
                        </div>
                        {/* Show Add Items button only if project has NO items yet */}
                        {!itemsImported && Number(viewData.linked_project_item_count || 0) === 0 ? (
                          <button onClick={addItemsToProject} disabled={addingItems}
                            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 text-sm font-bold transition-colors disabled:opacity-60">
                            <Package size={15}/> {addingItems ? 'Adding Items...' : 'Add PO Items to Project'}
                          </button>
                        ) : (
                          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-indigo-500 flex-shrink-0"/>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-indigo-700">Items in project: {viewData.linked_project_item_count || '—'}</p>
                              <p className="text-xs text-indigo-400">Items already exist in the linked project</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );

                    return (
                      <div>
                        {/* Button to open preview */}
                        {!showPreview && (
                          <button onClick={openPreview}
                            className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3 text-sm font-bold transition-colors">
                            <FolderPlus size={16}/>
                            Create Project from this PO
                            <ChevronRight size={15} className="ml-auto opacity-70"/>
                          </button>
                        )}

                        {/* Preview/Edit form */}
                        {showPreview && (
                          <div className="border-2 border-teal-300 bg-teal-50 rounded-2xl p-4 space-y-4">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 bg-teal-600 text-white rounded-lg flex items-center justify-center text-xs font-bold">✎</div>
                                <p className="font-bold text-teal-800 text-sm">Review & Edit Before Saving</p>
                              </div>
                              <button onClick={() => setShowPreview(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                            </div>

                            {/* Client info (read-only) */}
                            <div className="grid grid-cols-2 gap-3 bg-white rounded-xl p-3 border border-teal-100">
                              <div><p className="text-xs text-gray-400">Client</p><p className="text-sm font-semibold">{viewData.client_name}</p></div>
                              <div><p className="text-xs text-gray-400">PO Number</p><p className="text-sm font-semibold font-mono">{viewData.po_number}</p></div>
                            </div>

                            {/* Project Name */}
                            <div>
                              <label className="field-label">Project Name <span className="text-red-500">*</span></label>
                              <input className="field-input" placeholder="Enter project name"
                                value={previewName} onChange={e => setPreviewName(e.target.value)}/>
                            </div>

                            {/* Items — editable, exactly like Add New Project */}
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="field-label mb-0">Project Items <span className="text-red-500">*</span></label>
                                <button onClick={addItemRow}
                                  className="flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900 border border-teal-300 px-2 py-1 rounded-lg bg-white">
                                  <Plus size={12}/> Add Item
                                </button>
                              </div>
                              <div className="space-y-3">
                                {previewItems.map((it, idx) => (
                                  <div key={idx} className="bg-white rounded-xl border border-teal-100 p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-bold text-gray-500">Item #{idx+1}</span>
                                      {previewItems.length > 1 && (
                                        <button onClick={() => removeItemRow(idx)} className="text-red-400 hover:text-red-600">
                                          <Trash2 size={13}/>
                                        </button>
                                      )}
                                    </div>
                                    {/* Item Name */}
                                    <div>
                                      <label className="field-label">Item Name <span className="text-red-500">*</span></label>
                                      <input className="field-input" placeholder="e.g. Wardrobe, Dining Table"
                                        value={it.item_name}
                                        onChange={e => updateItem(idx, 'item_name', e.target.value)}/>
                                    </div>
                                    {/* Proto Code — per item */}
                                    <div>
                                      <label className="field-label">Proto Code</label>
                                      <input className="field-input" placeholder="e.g. MJ-5086"
                                        value={it.proto_code}
                                        onChange={e => updateItem(idx, 'proto_code', e.target.value)}/>
                                    </div>
                                    {/* Description */}
                                    <div>
                                      <label className="field-label">Description</label>
                                      <input className="field-input" placeholder="Optional description"
                                        value={it.description}
                                        onChange={e => updateItem(idx, 'description', e.target.value)}/>
                                    </div>
                                    {/* Qty + Unit + Rate + Discount */}
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="field-label">Quantity</label>
                                        <input type="number" min="1" className="field-input"
                                          value={it.quantity}
                                          onChange={e => updateItem(idx, 'quantity', e.target.value)}/>
                                      </div>
                                      <div>
                                        <label className="field-label">Unit</label>
                                        <select className="field-input" value={it.unit}
                                          onChange={e => updateItem(idx, 'unit', e.target.value)}>
                                          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="field-label">Rate (₹)</label>
                                        <input type="number" min="0" className="field-input" placeholder="0.00"
                                          value={it.rate}
                                          onChange={e => updateItem(idx, 'rate', e.target.value)}/>
                                      </div>
                                      <div>
                                        <label className="field-label">Discount (₹)</label>
                                        <input type="number" min="0" className="field-input" placeholder="0"
                                          value={it.discount||'0'}
                                          onChange={e => updateItem(idx, 'discount', e.target.value)}/>
                                      </div>
                                    </div>
                                    {/* Per-item auto calc */}
                                    {(parseFloat(it.rate)>0 || parseFloat(it.quantity)>0) && (() => {
                                      const lineTotal = (parseFloat(it.quantity)||0)*(parseFloat(it.rate)||0);
                                      const disc = parseFloat(it.discount)||0;
                                      const taxable = lineTotal - disc;
                                      return (
                                        <div className="bg-teal-50 rounded-lg p-2 text-xs space-y-0.5">
                                          <div className="flex justify-between"><span className="text-gray-500">Rate × Qty</span><span className="font-medium">{fmtCur(lineTotal)}</span></div>
                                          {disc>0 && <div className="flex justify-between text-red-500"><span>Discount</span><span>— {fmtCur(disc)}</span></div>}
                                          <div className="flex justify-between font-bold text-teal-700 border-t border-teal-100 pt-0.5"><span>Taxable Value</span><span>{fmtCur(taxable)}</span></div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Save Button */}
                            <button onClick={saveAndCreateProject} disabled={savingProject}
                              className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl py-3 text-sm font-bold transition-colors disabled:opacity-60">
                              <Save size={15}/>
                              {savingProject ? 'Creating Project...' : 'Save & Create Project'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Remark */}
                  {viewData.remark && (
                    <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                      <p className="text-xs text-amber-700 font-semibold mb-1">Remark</p>
                      <p className="text-sm text-gray-700">{viewData.remark}</p>
                    </div>
                  )}

                  {/* PDF */}
                  {viewData.pdf_url && (
                    <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <ExternalLink className="w-5 h-5 text-blue-600"/>
                      </div>
                      <div className="flex-1"><p className="text-sm font-semibold text-gray-900">Purchase Order PDF</p><p className="text-xs text-gray-400">Click to view or download</p></div>
                      <div className="flex gap-2">
                        <button onClick={() => openPdf(viewData.pdf_url)} className="btn-primary" style={{fontSize:12,padding:'7px 14px'}}><ExternalLink size={13}/> Open</button>
                        <button onClick={() => downloadPdf(viewData.pdf_url, `PO-${viewData.po_number}.pdf`)} className="btn-secondary" style={{fontSize:12,padding:'7px 14px'}}><Download size={13}/> Download</button>
                      </div>
                    </div>
                  )}

                  {/* Payments */}
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-3">Payment History</h3>
                    {(!viewData.payments || !viewData.payments.length) ? (
                      <div className="text-center py-6 bg-gray-50 rounded-xl text-gray-400 text-sm">No payments recorded yet</div>
                    ) : (
                      <div className="space-y-2">
                        {viewData.payments.map(p => (
                          <div key={p.id} className={`flex items-start justify-between gap-3 rounded-xl px-4 py-3 ${p.is_advance?'bg-purple-50 border border-purple-100':'bg-green-50 border border-green-100'}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold text-gray-900">{fmtCur(p.amount)}</span>
                                {p.is_advance ? <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-semibold">Advance</span>
                                  : <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">Payment</span>}
                                <span className="text-xs text-gray-500">{PM_LABELS[p.payment_mode]}</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {fmt(p.payment_date)}{p.bank_name&&` · ${p.bank_name}`}{p.utr_reference&&` · UTR: ${p.utr_reference}`}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400 flex-shrink-0">{fmt(p.created_at)}</span>
                          </div>
                        ))}
                        <div className="bg-gray-50 rounded-xl p-3 flex items-center justify-between gap-4 mt-2">
                          <div className="text-xs text-gray-500">Total Paid: <span className="font-bold text-green-600">{fmtCur(viewData.payments.reduce((s,p)=>s+Number(p.amount),0))}</span></div>
                          <div className="text-xs text-gray-500">Balance: <span className={`font-bold ${(viewData.total_amount-viewData.payments.reduce((s,p)=>s+Number(p.amount),0))>0?'text-red-500':'text-green-600'}`}>{fmtCur(viewData.total_amount-viewData.payments.reduce((s,p)=>s+Number(p.amount),0))}</span></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Sales Fulfillment ── */}
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-3">📊 Sales Fulfillment</h3>
                    {fulfillmentLoading ? (
                      <div className="text-center py-3 text-gray-400 text-xs">Loading fulfillment data...</div>
                    ) : fulfillment ? (
                      <div>
                        {/* Progress bar */}
                        <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 mb-3">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                            <span>Billed vs PO Amount</span>
                            <span className="font-semibold">{fulfillment.summary.billed_pct}%</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                            <div className={`h-2 rounded-full transition-all ${fulfillment.summary.billed_pct >= 100 ? 'bg-green-500' : 'bg-teal-500'}`}
                              style={{width:`${fulfillment.summary.billed_pct}%`}}/>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div className="text-center"><p className="text-gray-400">PO Total</p><p className="font-bold text-gray-800">{fmtCur(fulfillment.summary.po_total)}</p></div>
                            <div className="text-center"><p className="text-gray-400">Billed</p><p className="font-bold text-green-600">{fmtCur(fulfillment.summary.billed_total)}</p></div>
                            <div className="text-center"><p className="text-gray-400">Balance</p><p className={`font-bold ${fulfillment.summary.balance > 0 ? 'text-red-500' : 'text-green-600'}`}>{fmtCur(fulfillment.summary.balance)}</p></div>
                          </div>
                        </div>
                        {/* Linked invoices */}
                        {fulfillment.challans.length === 0 ? (
                          <div className="text-center py-4 bg-gray-50 rounded-xl text-gray-400 text-xs">
                            No sale invoices linked to this PO yet.<br/>
                            <span className="text-teal-600">In Sales Invoices, select this PO to link an invoice.</span>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {fulfillment.challans.map(ch => (
                              <div key={ch.id} className="bg-white border border-gray-100 rounded-xl p-3">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-xs font-bold text-purple-700 font-mono">{ch.challan_number}</span>
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-400">{fmt(ch.challan_date)}</span>
                                    <span className="text-xs font-semibold text-gray-800">{fmtCur(ch.total_amount)}</span>
                                  </div>
                                </div>
                                {ch.items?.length > 0 && (
                                  <div className="space-y-0.5">
                                    {ch.items.map((it, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1">
                                        <span className="font-medium truncate max-w-[140px]">{it.item_name}</span>
                                        <span className="text-gray-400 flex-shrink-0">{it.quantity} {it.unit} × {fmtCur(it.rate)} = <strong className="text-gray-700">{fmtCur(it.total)}</strong></span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-3 bg-gray-50 rounded-xl text-gray-400 text-xs">No fulfillment data</div>
                    )}
                  </div>

                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
